import React, { useEffect, useState } from "react";
import { Users as UsersIcon, Plus, Trash2, UserPlus } from "lucide-react";
import { toast } from "sonner";

import { v2Api } from "@/lib/v2Api";
import { confirm } from "@/lib/confirm";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

function NewUserDialog({ open, onOpenChange, roles, onCreated, isAdmin = false }) {
  const [form, setForm] = useState({ email: "", name: "", role_code: "member" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setForm({ email: "", name: "", role_code: "member" });
  }, [open]);
  const submit = async () => {
    if (!form.email.trim() || !form.name.trim()) { toast.error("email + name required"); return; }
    setBusy(true);
    try {
      await v2Api.createUser({ ...form, email: form.email.trim().toLowerCase(), name: form.name.trim() });
      toast.success("User added");
      onOpenChange(false);
      onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail ?? "Create failed");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>Invite user to workspace</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div><Label className="text-xs text-slate-500">Email</Label>
            <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="user@axisindia.in" />
          </div>
          <div><Label className="text-xs text-slate-500">Name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" />
          </div>
          {isAdmin && (
            <div><Label className="text-xs text-slate-500">Workspace role</Label>
              <select value={form.role_code} onChange={(e) => setForm({ ...form, role_code: e.target.value })}
                className="w-full px-2 py-1 border rounded-md bg-white">
                {roles.map((r) => <option key={r.code} value={r.code}>{r.name} ({r.code})</option>)}
              </select>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy}>{busy ? "Adding…" : "Add user"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function UsersPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canInvite = isAdmin || user?.role === "manager";
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openNew, setOpenNew] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const [u, r] = await Promise.all([v2Api.users(), v2Api.roles()]);
      setUsers(u || []); setRoles(r || []);
    } catch { toast.error("Could not load users"); }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  const changeRole = async (user, role_code) => {
    try { await v2Api.patchUser(user.id, { role_code }); refresh(); toast.success("Role updated"); }
    catch (err) { toast.error(err?.response?.data?.detail ?? "Update failed"); }
  };
  const toggleActive = async (user) => {
    try { await v2Api.patchUser(user.id, { is_active: !user.is_active }); refresh(); }
    catch (err) { toast.error("Update failed"); }
  };
  const remove = async (user) => {
    if (!await confirm(`Deactivate ${user.email}?`, { title: "Deactivate user", confirmLabel: "Deactivate" })) return;
    try { await v2Api.deleteUser(user.id); refresh(); }
    catch (err) { toast.error("Delete failed"); }
  };

  return (
    <div className="p-8 max-w-5xl mx-auto space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <UsersIcon className="w-5 h-5 text-blue-600" /> Users
          </h1>
          <p className="text-sm text-slate-500">
            Workspace users + their global role. Assign per-project roles from
            the Project Members tab.
          </p>
        </div>
        {canInvite && (
          <Button onClick={() => setOpenNew(true)}><Plus className="w-4 h-4 mr-1" /> Invite user</Button>
        )}
      </div>

      {loading && <p className="text-sm text-slate-500">Loading…</p>}

      <Card>
        <CardContent className="p-0">
          <div className="grid grid-cols-12 gap-2 px-4 py-2 text-[10px] uppercase tracking-wider text-slate-400 border-b bg-slate-50">
            <div className="col-span-3">Name</div>
            <div className="col-span-3">Email</div>
            <div className="col-span-3">Workspace role</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-1"></div>
          </div>
          {users.map((u) => (
            <div key={u.id} className="grid grid-cols-12 gap-2 px-4 py-2 border-b last:border-b-0 text-sm items-center hover:bg-slate-50">
              <div className="col-span-3 font-medium text-slate-800">{u.name}</div>
              <div className="col-span-3 text-slate-500 text-xs truncate">{u.email}</div>
              <div className="col-span-3">
                {isAdmin ? (
                  <select value={u.workspace_role_code || ""} onChange={(e) => changeRole(u, e.target.value)}
                    className="text-xs border rounded px-1 py-1 bg-white w-full">
                    <option value="">— none —</option>
                    {roles.map((r) => <option key={r.code} value={r.code}>{r.name}</option>)}
                  </select>
                ) : (
                  <span className="text-xs text-slate-600">{u.workspace_role_code || "—"}</span>
                )}
              </div>
              <div className="col-span-2">
                {isAdmin ? (
                  <button type="button" onClick={() => toggleActive(u)}>
                    {u.is_active
                      ? <Badge className="bg-emerald-100 text-emerald-700 cursor-pointer">Active</Badge>
                      : <Badge className="bg-slate-200 text-slate-600 cursor-pointer">Inactive</Badge>}
                  </button>
                ) : (
                  u.is_active
                    ? <Badge className="bg-emerald-100 text-emerald-700">Active</Badge>
                    : <Badge className="bg-slate-200 text-slate-600">Inactive</Badge>
                )}
              </div>
              <div className="col-span-1 text-right">
                {isAdmin && (
                  <button onClick={() => remove(u)} className="text-slate-300 hover:text-rose-500" title="Deactivate">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <NewUserDialog open={openNew} onOpenChange={setOpenNew} roles={roles} onCreated={refresh} isAdmin={isAdmin} />
    </div>
  );
}
